/**
 * Template T3 — Story-only "Big Title + Minimal Chips"
 * Full-bleed hero with large centered title. Story-optimized.
 */

import React from 'react';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { getPriceCopy } from '@/lib/events/priceCopy';
import type { ShareCardProps } from './ShareCardBase';
import {
  FARMLY_COLORS,
  FARMLY_FONTS,
  FarmlyBadge,
  FallbackBg,
  HeroImage,
} from './ShareCardBase';

export function TemplateStoryT3({ target, spec }: ShareCardProps) {
  const { w, h } = spec;

  let chipTexts: string[] = [];
  const isShop = target.type === 'shop';
  const isProducer = target.type === 'producer';
  if (target.type === 'activity') {
    if (target.startDate) {
      try {
        chipTexts.push(format(new Date(target.startDate), "d MMM", { locale: sv }));
      } catch { /* */ }
    }
    const price = getPriceCopy({ is_free: target.isFree, price_ore: target.priceOre });
    if (price.primary) chipTexts.push(price.primary);
  } else if (target.type === 'product') {
    chipTexts.push((target.priceOre / 100).toLocaleString('sv-SE') + ' kr');
  } else if (target.type === 'shop') {
    chipTexts.push('Gårdsbutik');
    if (target.openingSnippet) chipTexts.push(target.openingSnippet);
  } else if (target.type === 'producer') {
    if (target.placeName) chipTexts.push(`📍 ${target.placeName}`);
    chipTexts.push('farmly.se');
  }

  return (
    <div
      style={{
        position: 'relative',
        width: w,
        height: h,
        overflow: 'hidden',
        fontFamily: FARMLY_FONTS.body,
      }}
    >
      {/* Background */}
      {target.heroImageUrl ? (
        <HeroImage src={target.heroImageUrl} w={w} h={h} />
      ) : (
        <FallbackBg title={target.title} w={w} h={h} variant={(isShop || isProducer) ? 'place' : 'default'} />
      )}

      {/* Full overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.45)',
          zIndex: 1,
        }}
      />

      {/* Centered content */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: 56,
          gap: 24,
        }}
      >
        {/* Producer */}
        <div
          style={{
            fontSize: 20,
            color: FARMLY_COLORS.warmSand,
            opacity: 0.8,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            fontWeight: 500,
          }}
        >
          {target.producerName}
        </div>

        {/* Big title */}
        <div
          style={{
            fontFamily: FARMLY_FONTS.display,
            fontSize: 64,
            lineHeight: 1.1,
            color: FARMLY_COLORS.white,
            maxWidth: w - 112,
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
          }}
        >
          {target.title}
        </div>

        {/* Chips */}
        {chipTexts.length > 0 && (
          <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
            {chipTexts.map((chip, i) => (
              <div
                key={i}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: 20,
                  padding: '8px 20px',
                  fontSize: 20,
                  color: FARMLY_COLORS.white,
                  fontWeight: 500,
                }}
              >
                {chip}
              </div>
            ))}
          </div>
        )}
      </div>

      <FarmlyBadge style={{ bottom: 80 }} />
    </div>
  );
}
